import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { AddCircle, Clear, ContentCopy, Delete, Label, LabelOff, Refresh, Search } from '@mui/icons-material';
import { Box, Button, Chip, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import { DataGrid, GridActionsCellItem, GridColDef, GridFilterModel, GridSortModel } from '@mui/x-data-grid';

import { FlowClient } from '@@api';
import { useApiClient, useCopyToClipboard, useIsMobile, usePersistedSetting, usePersistedTablePagination, useToastService } from '@@common';
import { ConfirmDialog, GalleryHeader, GalleryStatistics, getResponsiveDataGridProps } from '@@components';

import { FlowViewModel } from '../../api/api';
import { formatTimestampFull } from '../../common/formatTimestamp';
import useDebouncedCallback from '../../common/hooks/useDebouncedCallback';
import { PresenceIndicator } from '../../components/Presence/PresenceIndicator';
import { usePresence } from '../../components/Presence/usePresence';

const filterDebounceMs = 300;
const pageSizeOptions = [10, 20, 50];
const initialFilterModel = {
	items: [],
};
const initialSortModel: GridSortModel = [{ field: 'name', sort: 'asc' }];

const sortFieldName: Record<string, string> = {
	name: 'Name',
};

function FlowViewers({ flowId }: { flowId: string }) {
	const viewers = usePresence('flow', flowId, 'definition', null, true);
	return <PresenceIndicator viewers={viewers} />;
}

export default function FlowListView() {
	const navigate = useNavigate();
	const client = useApiClient(FlowClient);
	const queryClient = useQueryClient();
	const toastService = useToastService();
	const copyToClipboard = useCopyToClipboard();
	const isMobile = useIsMobile();

	const { paginationModel, setPaginationModel } = usePersistedTablePagination('flowList.pageSize');
	const [sortModel, setSortModel] = useState<GridSortModel>(initialSortModel);

	const [filterModel, setFilterModel] = useState<GridFilterModel>(initialFilterModel);
	const [nameFilter, setNameFilter] = useState('');
	const [debouncedNameFilter, setDebouncedNameFilter] = useState('');
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [selectedFlow, setSelectedFlow] = useState<FlowViewModel | null>(null);

	// Tag filter state
	const [includeTags, setIncludeTags] = usePersistedSetting<string[]>('flowList.includeTags', []);
	const [excludeTags, setExcludeTags] = usePersistedSetting<string[]>('flowList.excludeTags', []);
	const [includeTagInput, setIncludeTagInput] = useState('');
	const [excludeTagInput, setExcludeTagInput] = useState('');

	// Add tag inline state
	const [addingTagForFlowId, setAddingTagForFlowId] = useState<string | null>(null);
	const [newTagInput, setNewTagInput] = useState('');
	const addTagInputRef = useRef<HTMLInputElement>(null);
	const normalizedNameFilter = debouncedNameFilter.trim();

	const updateNameFilter = useDebouncedCallback((value: string) => {
		setDebouncedNameFilter(value);
		setPaginationModel((current) => ({ ...current, page: 0 }));
	}, filterDebounceMs);

	const { data, isLoading, refetch } = useQuery({
		queryKey: ['flow', 'list', paginationModel, sortModel, filterModel, normalizedNameFilter, includeTags, excludeTags],
		queryFn: () => {
			const sortBy = sortModel.length > 0 ? sortFieldName[sortModel[0].field] : undefined;
			const sortDirection = sortModel.length > 0 ? (sortModel[0].sort === 'asc' ? true : false) : undefined;
			return client.search(
				paginationModel.page,
				paginationModel.pageSize,
				sortBy,
				sortDirection,
				normalizedNameFilter || undefined,
				includeTags.length > 0 ? includeTags : undefined,
				excludeTags.length > 0 ? excludeTags : undefined,
			);
		},
	});

	const addTagMutation = useMutation({
		mutationFn: ({ id, tag }: { id: string; tag: string }) => client.addTag(id, tag),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['flow', 'list'] }),
		onError: (error) => {
			const msg = error instanceof Error ? error.message : 'Unknown error';
			toastService.error(`Failed to add tag: ${msg}`);
		},
	});

	const removeTagMutation = useMutation({
		mutationFn: ({ id, tag }: { id: string; tag: string }) => client.removeTag(id, tag),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['flow', 'list'] }),
		onError: (error) => {
			const msg = error instanceof Error ? error.message : 'Unknown error';
			toastService.error(`Failed to remove tag: ${msg}`);
		},
	});

	const rowCount = useMemo(
		() => (data ? paginationModel.page * paginationModel.pageSize + (data.hasNextPage ? paginationModel.pageSize + 1 : data.items.length) : 0),
		[paginationModel, data],
	);
	const flows = data?.items ?? [];
	const systemFlowCount = flows.filter((flow) => flow.isReadOnly).length;
	const taggedFlowCount = flows.filter((flow) => (flow.tags?.length ?? 0) > 0).length;

	const handleDeleteFlow = (flow: FlowViewModel) => {
		if (flow.isReadOnly) {
			return;
		}

		setSelectedFlow(flow);
		setOpenDeleteDialog(true);
	};

	const handleConfirmDelete = async () => {
		if (!selectedFlow) return;

		try {
			await client.delete(selectedFlow.id);
			toastService.success(`Flow "${selectedFlow.name}" deleted successfully.`);
			refetch();
		} catch (error) {
			console.error('Error deleting flow:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to delete flow: ${errorMessage}`);
		} finally {
			setOpenDeleteDialog(false);
			setSelectedFlow(null);
		}
	};

	const handleCancelDelete = () => {
		setOpenDeleteDialog(false);
		setSelectedFlow(null);
	};

	const handleCopyId = (flow: FlowViewModel) => {
		copyToClipboard(flow.id, (id) => String(id));
	};

	const addIncludeTag = () => {
		const tag = includeTagInput.trim();
		if (tag && !includeTags.includes(tag)) {
			setIncludeTags((prev) => [...prev, tag]);
			setPaginationModel((m) => ({ ...m, page: 0 }));
		}
		setIncludeTagInput('');
	};

	const addExcludeTag = () => {
		const tag = excludeTagInput.trim();
		if (tag && !excludeTags.includes(tag)) {
			setExcludeTags((prev) => [...prev, tag]);
			setPaginationModel((m) => ({ ...m, page: 0 }));
		}
		setExcludeTagInput('');
	};

	const commitAddTag = async (flowId: string) => {
		const tag = newTagInput.trim();
		if (tag) {
			await addTagMutation.mutateAsync({ id: flowId, tag });
		}
		setAddingTagForFlowId(null);
		setNewTagInput('');
	};

	const columns: GridColDef<FlowViewModel>[] = [
		{
			field: 'name',
			headerName: 'Name',
			sortable: true,
			flex: 1,
			renderCell: (params) => (
				<Stack direction="row" alignItems="center" spacing={1} sx={{ height: '100%' }}>
					<Link to={`/flow/${params.row.id}`} style={{ color: 'inherit' }}>
						{params.value}
					</Link>
					<FlowViewers flowId={params.row.id} />
					{params.row.isReadOnly && <Chip label="System" color="info" variant="outlined" size="small" />}
				</Stack>
			),
		},
		{
			field: 'updatedDate',
			headerName: 'Updated',
			width: 200,
			sortable: false,
			valueFormatter: (value: string) => (value ? formatTimestampFull(value) : ''),
		},
		{
			field: 'updatedByName',
			headerName: 'Updated By',
			width: 150,
			sortable: false,
			valueGetter: (_value, row) => row.updatedByName ?? row.updatedById ?? row.createdByName ?? row.createdById ?? '',
		},
		{
			field: 'tags',
			headerName: 'Tags',
			width: 350,
			sortable: false,
			renderCell: (params) => (
				<Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ py: 0.5 }}>
					{(params.row.tags ?? []).map((tag) => (
						<Chip
							key={tag}
							label={tag}
							size="small"
							onDelete={params.row.isReadOnly ? undefined : () => removeTagMutation.mutate({ id: params.row.id, tag })}
						/>
					))}
					{!params.row.isReadOnly && addingTagForFlowId === params.row.id ? (
						<TextField
							inputRef={addTagInputRef}
							size="small"
							variant="outlined"
							placeholder="Tag name"
							value={newTagInput}
							autoFocus
							sx={{ width: 120, '& .MuiInputBase-input': { fontSize: '0.75rem', py: '4px' } }}
							onChange={(e) => setNewTagInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') commitAddTag(params.row.id);
								if (e.key === 'Escape') {
									setAddingTagForFlowId(null);
									setNewTagInput('');
								}
							}}
							onBlur={() => commitAddTag(params.row.id)}
							slotProps={{
								input: {
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												size="small"
												onMouseDown={(e) => {
													e.preventDefault();
													commitAddTag(params.row.id);
												}}
											>
												<AddCircle fontSize="small" />
											</IconButton>
										</InputAdornment>
									),
								},
							}}
						/>
					) : !params.row.isReadOnly ? (
						<Tooltip title="Add tag">
							<IconButton
								size="small"
								onClick={() => {
									setAddingTagForFlowId(params.row.id);
									setNewTagInput('');
								}}
							>
								<AddCircle fontSize="small" />
							</IconButton>
						</Tooltip>
					) : null}
				</Stack>
			),
		},
		{
			field: 'actions',
			type: 'actions',
			headerName: 'Actions',
			width: 100,
			getActions: (params) => [
				<GridActionsCellItem icon={<ContentCopy />} label="Copy ID" onClick={() => handleCopyId(params.row)} showInMenu />,
				<GridActionsCellItem icon={<Delete />} label="Delete" disabled={params.row.isReadOnly} onClick={() => handleDeleteFlow(params.row)} showInMenu />,
			],
		},
	];

	const tagFilters = (
		<Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
			<Stack direction="column" spacing={0.5} sx={{ minWidth: { md: 176 } }}>
				<Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					<Label fontSize="inherit" /> Include tags
				</Typography>
				<Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
					{includeTags.map((tag) => (
						<Chip key={tag} label={tag} size="small" color="success" variant="outlined" onDelete={() => setIncludeTags((prev) => prev.filter((t) => t !== tag))} />
					))}
					<TextField
						size="small"
						placeholder="Add tag…"
						value={includeTagInput}
						onChange={(event) => setIncludeTagInput(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === 'Enter') addIncludeTag();
						}}
						sx={{ width: { xs: '100%', md: 176 }, '& .MuiInputBase-input': { fontSize: '0.75rem', py: '4px' } }}
						slotProps={{
							input: {
								endAdornment: (
									<InputAdornment position="end">
										<IconButton size="small" onClick={addIncludeTag} disabled={!includeTagInput.trim()}>
											<AddCircle fontSize="small" />
										</IconButton>
									</InputAdornment>
								),
							},
						}}
					/>
				</Stack>
			</Stack>
			<Stack direction="column" spacing={0.5} sx={{ minWidth: { md: 176 } }}>
				<Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					<LabelOff fontSize="inherit" /> Exclude tags
				</Typography>
				<Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
					{excludeTags.map((tag) => (
						<Chip key={tag} label={tag} size="small" color="error" variant="outlined" onDelete={() => setExcludeTags((prev) => prev.filter((t) => t !== tag))} />
					))}
					<TextField
						size="small"
						placeholder="Add tag…"
						value={excludeTagInput}
						onChange={(event) => setExcludeTagInput(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === 'Enter') addExcludeTag();
						}}
						sx={{ width: { xs: '100%', md: 176 }, '& .MuiInputBase-input': { fontSize: '0.75rem', py: '4px' } }}
						slotProps={{
							input: {
								endAdornment: (
									<InputAdornment position="end">
										<IconButton size="small" onClick={addExcludeTag} disabled={!excludeTagInput.trim()}>
											<AddCircle fontSize="small" />
										</IconButton>
									</InputAdornment>
								),
							},
						}}
					/>
				</Stack>
			</Stack>
			{(nameFilter.trim() || includeTags.length > 0 || excludeTags.length > 0) && (
				<Button
					size="small"
					variant="outlined"
					color="inherit"
					onClick={() => {
						setNameFilter('');
						setDebouncedNameFilter('');
						updateNameFilter('');
						setIncludeTags([]);
						setExcludeTags([]);
						setPaginationModel((current) => ({ ...current, page: 0 }));
					}}
					sx={{ alignSelf: { xs: 'stretch', md: 'center' }, fontSize: '0.75rem' }}
				>
					Clear filters
				</Button>
			)}
		</Stack>
	);

	return (
		<Stack direction="column" spacing={2} sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0, minHeight: 0, p: (theme) => `${theme.layout.edgeGap}px`, overflow: 'hidden' }}>
			<GalleryHeader
				title="Flows"
				subtitle="Design, organize, and run your automation flows."
				utilityActions={
					<Tooltip title="Refresh flows">
						<IconButton aria-label="Refresh flows" onClick={() => refetch()}>
							<Refresh />
						</IconButton>
					</Tooltip>
				}
				primaryAction={
					<Button variant="contained" startIcon={<AddCircle />} onClick={() => navigate('/flow/new')}>
						Create flow
					</Button>
				}
				statistics={
					<GalleryStatistics
						items={[
							{ label: 'Flows on this page', value: flows.length },
							{ label: 'System flows', value: systemFlowCount },
							{ label: 'Tagged flows', value: taggedFlowCount },
						]}
					/>
				}
				search={
					<TextField
						size="small"
						placeholder="Search flows…"
						value={nameFilter}
						fullWidth
						onChange={(event) => {
							setNameFilter(event.target.value);
							updateNameFilter(event.target.value);
						}}
						InputProps={{
							startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
							endAdornment: nameFilter ? (
								<InputAdornment position="end">
									<IconButton aria-label="Clear flow search" size="small" onClick={() => { setNameFilter(''); updateNameFilter(''); }}>
										<Clear fontSize="small" />
									</IconButton>
								</InputAdornment>
							) : undefined,
						}}
						inputProps={{ 'aria-label': 'Search flows by name' }}
					/>
				}
				searchActions={tagFilters}
			/>

			<Paper sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
				<DataGrid
					loading={isLoading}
					rows={data?.items}
					rowCount={rowCount}
					columns={columns}
					paginationMode="server"
					sortingMode="server"
					disableColumnFilter
					pageSizeOptions={pageSizeOptions}
					paginationModel={paginationModel}
					onPaginationModelChange={setPaginationModel}
					sortModel={sortModel}
					onSortModelChange={setSortModel}
					filterModel={filterModel}
					onFilterModelChange={setFilterModel}
					columnVisibilityModel={{
						updatedDate: !isMobile,
						updatedByName: !isMobile,
						tags: !isMobile,
					}}
					{...getResponsiveDataGridProps({
						isMobile,
						sx: {
							color: 'text.primary',
							'& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', color: 'text.primary' },
							'& .MuiDataGrid-columnHeaderTitle': { color: 'text.primary' },
						},
					})}
					getRowHeight={() => 'auto'}
				/>
			</Paper>
			<ConfirmDialog
				open={openDeleteDialog}
				title="Delete Flow"
				message={
					selectedFlow
						? `Are you sure you want to delete "${selectedFlow.name}"? This action cannot be undone and may affect any operations or integrations that depend on it.`
						: ''
				}
				confirmText="Delete"
				confirmColor="error"
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>
		</Stack>
	);
}
